/**
 * Modèle d'accès de l'Extranet API v1.8.4 : profils (`role`), périmètre (`scopes`)
 * et domaines fonctionnels qu'ils débloquent.
 *
 * Source unique pour toute l'UI — gating des routes, sidebar, éditeur d'accès,
 * filtres. Aucun import : ce module doit rester importable de partout.
 */

export const EXTRANET_ROLES = [
  'Extranet_Admin',
  'Extranet_Achat',
  'Extranet_Commande',
  'Extranet_Facturation',
] as const;
export type ExtranetRole = (typeof EXTRANET_ROLES)[number];

export const SCOPE_KEYS = [
  'billingReference1',
  'billingReference2',
  'billingReference3',
  'costCenter',
  'serial',
] as const;
export type ScopeKey = (typeof SCOPE_KEYS)[number];

export interface RoleScope {
  key: ScopeKey;
  values: string[];
}

/**
 * Un rôle et son périmètre. `scopes: []` = tout le compte.
 * `role` reste un `string` comme dans le contrat : un rôle inconnu du front
 * doit être conservé tel quel (cf. `normalizeAccess`), pas rejeté.
 */
export interface RoleGrant {
  role: string;
  scopes: RoleScope[];
}

export const DOMAINS = ['admin', 'invoicing', 'trips', 'incidents', 'performance'] as const;
export type Domain = (typeof DOMAINS)[number];

/** Matrice profil → domaines de la doc « Rôles & accès ». */
export const ROLE_DOMAINS: Record<ExtranetRole, readonly Domain[]> = {
  Extranet_Admin: ['admin', 'invoicing', 'trips', 'incidents', 'performance'],
  Extranet_Achat: ['invoicing', 'trips', 'incidents', 'performance'],
  Extranet_Commande: ['trips', 'incidents'],
  Extranet_Facturation: ['invoicing'],
};

export const ROLE_LABELS: Record<ExtranetRole, string> = {
  Extranet_Admin: 'Administrateur',
  Extranet_Achat: 'Achats',
  Extranet_Commande: 'Commande',
  Extranet_Facturation: 'Facturation',
};

export const DOMAIN_LABELS: Record<Domain, string> = {
  admin: 'Administration',
  invoicing: 'Facturation',
  trips: 'Courses',
  incidents: 'Incidents',
  performance: 'Performance',
};

export const SCOPE_KEY_LABELS: Record<ScopeKey, string> = {
  billingReference1: 'Réf. facturation 1 (site / BUPO)',
  billingReference2: 'Réf. facturation 2 (contrat / CTR)',
  billingReference3: 'Réf. facturation 3',
  costCenter: 'Centre de coût',
  serial: 'Matricule',
};

/** Page d'atterrissage par domaine, par ordre de préférence. */
export const DOMAIN_HOME: ReadonlyArray<readonly [Domain, string]> = [
  ['trips', '/'],
  ['invoicing', '/invoices'],
  ['incidents', '/incidents'],
  ['performance', '/performance'],
  ['admin', '/admin'],
];

export const isKnownRole = (role: string): role is ExtranetRole => (EXTRANET_ROLES as readonly string[]).includes(role);

export const domainsOf = (access: readonly RoleGrant[]): ReadonlySet<Domain> => {
  const domains = new Set<Domain>();
  for (const grant of access) {
    if (!isKnownRole(grant.role)) continue;
    for (const domain of ROLE_DOMAINS[grant.role]) domains.add(domain);
  }
  return domains;
};

export const isAdmin = (access: readonly RoleGrant[]): boolean => access.some(grant => grant.role === 'Extranet_Admin');

export const firstAccessibleRoute = (domains: ReadonlySet<Domain>): string | null =>
  DOMAIN_HOME.find(([domain]) => domains.has(domain))?.[1] ?? null;

/** Une dimension de filtrage inconnue du front s'affiche telle quelle plutôt que vide. */
export const scopeKeyLabel = (key: string): string => SCOPE_KEY_LABELS[key as ScopeKey] ?? key;

export const roleLabel = (role: string): string => (isKnownRole(role) ? ROLE_LABELS[role] : role);

export const roleDescription = (role: ExtranetRole): string =>
  ROLE_DOMAINS[role].map(domain => DOMAIN_LABELS[domain]).join(' · ');

export const scopeSummary = (grant: RoleGrant): string => {
  const count = grant.scopes.filter(scope => scope.values.length > 0).length;
  if (count === 0) return 'Tout le compte';
  return count === 1 ? '1 restriction' : `${count} restrictions`;
};

/** Valeurs de périmètre déjà attribuées à l'appelant sur une dimension — proposées en suggestion de filtre. */
export const scopeSuggestions = (access: readonly RoleGrant[], key: ScopeKey): string[] => {
  const values = new Set<string>();
  for (const grant of access) {
    for (const scope of grant.scopes) {
      if (scope.key === key) scope.values.forEach(value => values.add(value));
    }
  }
  return Array.from(values).sort();
};

/**
 * Nettoie un jeu d'accès avant envoi : un seul grant par rôle (les périmètres
 * sont fusionnés), valeurs trimées et dédoublonnées, scopes sans valeur retirés.
 */
export const normalizeAccess = (access: readonly RoleGrant[]): RoleGrant[] => {
  const byRole = new Map<string, Map<ScopeKey, Set<string>>>();
  for (const grant of access) {
    const scopes = byRole.get(grant.role) ?? new Map<ScopeKey, Set<string>>();
    for (const scope of grant.scopes) {
      const values = scopes.get(scope.key) ?? new Set<string>();
      scope.values
        .map(value => value.trim())
        .filter(Boolean)
        .forEach(value => values.add(value));
      scopes.set(scope.key, values);
    }
    byRole.set(grant.role, scopes);
  }
  return Array.from(byRole, ([role, scopes]) => ({
    role,
    scopes: Array.from(scopes, ([key, values]) => ({ key, values: Array.from(values) })).filter(
      scope => scope.values.length > 0,
    ),
  }));
};
